import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, AlertTriangle, RefreshCw, CheckCircle, ScanLine, XCircle, Clock, Info, Search, X, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const CancelledPickSlipPutAway = () => {
  const navigate = useNavigate();
  const scanInputRef = useRef(null);
  
  // State
  const [loading, setLoading] = useState(true);
  const [pendingItems, setPendingItems] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [binCode, setBinCode] = useState('');
  const [putAwayLoading, setPutAwayLoading] = useState(false);
  const [showPutAwayDialog, setShowPutAwayDialog] = useState(false);
  const [suggestedBin, setSuggestedBin] = useState(null);
  const [suggestionReason, setSuggestionReason] = useState('');
  const [alternativeBins, setAlternativeBins] = useState([]);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [search, setSearch] = useState('');

  // --- Create Pack: re-group returned pieces into a 5-pc pack ---
  const [packSize, setPackSize] = useState(5);
  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [packPieces, setPackPieces] = useState([]);
  const [packPieceInput, setPackPieceInput] = useState('');
  const [packCode, setPackCode] = useState('');
  const [packScanning, setPackScanning] = useState(false);
  const [packCreating, setPackCreating] = useState(false);
  const packPieceRef = useRef(null);
  const packCodeRef = useRef(null);
  const packLockSku = packPieces[0]?.sku_code || '';

  useEffect(() => {
    loadPendingItems();
    apiClient.get('/wms/fg/packs/config').then((r) => setPackSize(r.data.pack_size || 5)).catch(() => {});
  }, []);

  useEffect(() => {
    if (packDialogOpen) setTimeout(() => packPieceRef.current?.focus(), 120);
  }, [packDialogOpen]);
  
  useEffect(() => {
    if (showPutAwayDialog && scanInputRef.current) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [showPutAwayDialog]);
  
  const loadPendingItems = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/wms/fg/put-away/cancelled-pickslip-pending');
      setPendingItems(response.data.pending_items || []);
      setTotalPending(response.data.total_pending || 0);
    } catch (error) {
      console.error('Error loading pending items:', error);
      toast.error('Failed to load pending items');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectItem = async (item) => {
    setSelectedItem(item);
    setBinCode('');
    setSuggestedBin(null);
    setSuggestionReason('');
    setAlternativeBins([]);
    setShowPutAwayDialog(true);
    
    // Fetch suggested bin using the new endpoint
    setLoadingSuggestion(true);
    try {
      const usnCode = item.usn_code || item.pack_barcode;
      const response = await apiClient.get(`/wms/fg/put-away/suggest-bin-for-cancelled-item/${encodeURIComponent(usnCode)}`);
      
      if (response.data.suggested_bin) {
        setSuggestedBin(response.data.suggested_bin);
        setSuggestionReason(response.data.suggestion_reason || '');
        setBinCode(response.data.suggested_bin); // Auto-fill suggested bin
      }
      
      if (response.data.alternative_bins?.length > 0) {
        setAlternativeBins(response.data.alternative_bins);
      }
    } catch (error) {
      // Suggestion failed, user will scan bin manually
      console.log('Could not get suggested bin:', error);
    } finally {
      setLoadingSuggestion(false);
    }
  };
  
  const handlePutAway = async () => {
    if (!binCode.trim()) {
      toast.error('Please scan or enter bin code');
      return;
    }
    
    setPutAwayLoading(true);
    try {
      const usnCode = selectedItem.usn_code || selectedItem.pack_barcode;
      const response = await apiClient.post(
        `/wms/fg/put-away/put-away-cancelled-item?usn_code=${encodeURIComponent(usnCode)}&bin_code=${encodeURIComponent(binCode.trim())}`
      );
      
      if (response.data.success) {
        toast.success(`Item stored in bin ${binCode}`);
        const putUsn = selectedItem.usn_code || selectedItem.pack_barcode;
        setShowPutAwayDialog(false);
        setSelectedItem(null);
        setBinCode('');
        // Remove the item locally (instant) instead of a full network reload —
        // reloading the whole cancelled queue after every scan was the main lag.
        setPendingItems((groups) =>
          groups
            .map((g) => ({
              ...g,
              items: g.items.filter((it) => (it.usn_code || it.pack_barcode) !== putUsn),
            }))
            .filter((g) => g.items.length > 0)
        );
        setTotalPending((n) => Math.max(0, n - 1));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to put away item';
      toast.error(errorMsg);
    } finally {
      setPutAwayLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePutAway();
    }
  };

  // ---- Create Pack handlers ----
  const scanPackPiece = async (e) => {
    e?.preventDefault();
    const usn = packPieceInput.trim();
    if (!usn || packScanning) return;
    if (packPieces.some((p) => p.usn_code === usn)) {
      toast.error(`${usn} already scanned`);
      setPackPieceInput('');
      return;
    }
    if (packPieces.length >= packSize) {
      toast.error(`Pack full (${packSize}) — scan the pack code`);
      setPackPieceInput('');
      setTimeout(() => packCodeRef.current?.focus(), 80);
      return;
    }
    setPackScanning(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/validate-piece', { usn_code: usn, lock_sku: packLockSku, context: 'cancelled' });
      const next = [...packPieces, r.data];
      setPackPieces(next);
      setPackPieceInput('');
      if (next.length >= packSize) {
        toast.success('Pack complete — scan the pack code');
        setTimeout(() => packCodeRef.current?.focus(), 80);
      } else {
        setTimeout(() => packPieceRef.current?.focus(), 80);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Piece not eligible');
      setPackPieceInput('');
    } finally {
      setPackScanning(false);
    }
  };

  const removePackPiece = (usn) => setPackPieces((prev) => prev.filter((p) => p.usn_code !== usn));

  const submitRepack = async (e) => {
    e?.preventDefault();
    const code = packCode.trim();
    if (!code) { toast.error('Scan the pre-printed pack code'); packCodeRef.current?.focus(); return; }
    if (!packPieces.length) { toast.error('Scan at least one returned piece first'); return; }
    if (packCreating) return;
    setPackCreating(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/repack-cancelled', { pack_code: code, usn_codes: packPieces.map((p) => p.usn_code) });
      toast.success(r.data.message || 'Pack created');
      const packedUsns = packPieces.map((p) => p.usn_code);
      setPendingItems((groups) => groups.map((g) => ({
        ...g,
        items: g.items.map((it) => packedUsns.includes(it.usn_code || it.pack_barcode) ? { ...it, pack_code: code } : it),
      })));
      setPackPieces([]);
      setPackCode('');
      setPackDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create pack');
    } finally {
      setPackCreating(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Global client-side filter across slip + item fields.
  const q = search.trim().toLowerCase();
  const matchItem = (it) => {
    if (!q) return true;
    return [it.usn_code, it.pack_barcode, it.item_barcode, it.design_name, it.shade, it.size, it.original_bin]
      .some((v) => (v || '').toString().toLowerCase().includes(q));
  };
  const filteredGroups = (pendingItems || []).map((g) => {
    const slipMatch = q && [g.cancelled_from_slip, g.cancellation_reason].some((v) => (v || '').toString().toLowerCase().includes(q));
    const items = (g.items || []).filter((it) => slipMatch || matchItem(it));
    return { ...g, items };
  }).filter((g) => !q || (g.items && g.items.length > 0));
  const filteredItemCount = filteredGroups.reduce((n, g) => n + (g.items?.length || 0), 0);
  
  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-600 to-orange-800 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/wms/mobile')}
            className="text-white hover:bg-white/20 -ml-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="text-center flex-1">
            <h1 className="text-xl font-bold">Cancelled Pick Slip Queue</h1>
            <p className="text-sm text-orange-200">Put away items from cancelled slips</p>
          </div>
          <Button
            variant="ghost"
            onClick={() => setPackDialogOpen(true)}
            className="text-white hover:bg-white/20 gap-1 text-sm"
            data-testid="cancelled-create-pack-btn"
          >
            <Boxes className="w-5 h-5" /> Pack
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={loadPendingItems}
            disabled={loading}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Stats Bar */}
        <div className="mt-4 flex items-center justify-center gap-4">
          <div className="bg-white/20 rounded-xl px-4 py-2 text-center">
            <p className="text-2xl font-bold">{totalPending}</p>
            <p className="text-xs text-orange-200">Pending Items</p>
          </div>
          <div className="bg-white/20 rounded-xl px-4 py-2 text-center">
            <p className="text-2xl font-bold">{pendingItems.length}</p>
            <p className="text-xs text-orange-200">Cancelled Slips</p>
          </div>
        </div>

        {/* Global filter */}
        <div className="mt-4 max-w-xl mx-auto">
          <div className="relative">
            <Search className="w-4 h-4 text-orange-200 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by USN, SKU, design, bin, slip no or reason…"
              className="pl-9 pr-9 h-11 bg-white/15 border-white/25 text-white placeholder:text-orange-200/80 rounded-xl"
              data-testid="cancelled-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-orange-200 hover:text-white"
                data-testid="cancelled-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {search && (
            <p className="text-xs text-orange-200 mt-1 text-center" data-testid="cancelled-search-count">
              {filteredItemCount} item{filteredItemCount === 1 ? '' : 's'} in {filteredGroups.length} slip{filteredGroups.length === 1 ? '' : 's'} match
            </p>
          )}
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 p-4 overflow-auto">
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-orange-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading pending items...</p>
            </div>
          </div>
        ) : totalPending === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-green-400">All Clear!</h3>
            <p className="text-gray-400 mt-2 text-center">
              No pending items from cancelled pick slips
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/wms/mobile')}
              className="mt-6 border-gray-600"
            >
              Back to Dashboard
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Info Alert */}
            <Alert className="bg-orange-500/10 border-orange-500/30">
              <Info className="h-4 w-4 text-orange-400" />
              <AlertDescription className="text-orange-200 text-sm">
                These items were picked but the pick slip was cancelled. 
                Please put them away to the appropriate bins.
              </AlertDescription>
            </Alert>
            
            {/* Group by Cancelled Slip */}
            {filteredGroups.length === 0 ? (
              <div className="text-center py-12 text-gray-400" data-testid="cancelled-no-results">
                No items match &quot;{search}&quot;.
              </div>
            ) : filteredGroups.map((slipGroup, idx) => (
              <Card key={idx} className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        {slipGroup.cancelled_from_slip}
                      </CardTitle>
                      {slipGroup.cancellation_reason && (
                        <p className="text-xs text-gray-400 mt-1">
                          Reason: {slipGroup.cancellation_reason}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="border-orange-500/50 text-orange-400">
                      {slipGroup.items?.length || 0} items
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="space-y-2">
                    {slipGroup.items?.map((item, itemIdx) => (
                      <div 
                        key={itemIdx}
                        onClick={() => handleSelectItem(item)}
                        className="bg-gray-700/50 rounded-xl p-3 cursor-pointer hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-mono font-bold text-sm">
                              {item.usn_code || item.pack_barcode}
                            </p>
                            <p className="text-xs text-gray-400">{item.item_barcode}</p>
                            {item.design_name && (
                              <p className="text-xs text-gray-500">{item.design_name}</p>
                            )}
                            {item.pack_code && (
                              <Badge variant="outline" className="mt-1 bg-fuchsia-600/20 text-fuchsia-300 border-fuchsia-500/40" data-testid={`item-pack-badge-${itemIdx}`}>
                                <Boxes className="w-3 h-3 mr-1" /> Pack {item.pack_code}
                              </Badge>
                            )}
                            {/* 2026-06-16 — show true ageing so operators
                                FIFO the oldest packs first. Computed from
                                original_put_away_date which is preserved
                                across restickers / bin-to-bin / cancelled
                                returns. */}
                            {item.original_put_away_date && (() => {
                              const days = Math.max(0, Math.floor(
                                (Date.now() - new Date(item.original_put_away_date).getTime())
                                / (1000 * 60 * 60 * 24)
                              ));
                              const cls = days >= 60
                                ? 'text-red-400 font-semibold'
                                : days >= 30
                                  ? 'text-amber-400'
                                  : 'text-emerald-400';
                              return (
                                <p className={`text-[10px] mt-0.5 ${cls}`} data-testid={`age-${itemIdx}`}>
                                  Age: {days} day{days === 1 ? '' : 's'}
                                  {item.re_put_away_count > 0 && (
                                    <span className="text-gray-500 ml-1">
                                      · re-put-away {item.re_put_away_count}×
                                    </span>
                                  )}
                                </p>
                              );
                            })()}
                          </div>
                          <div className="text-right">
                            {item.original_bin && (
                              <div className="flex items-center gap-1 text-xs text-gray-400">
                                <MapPin className="w-3 h-3" />
                                <span>Was: {item.original_bin}</span>
                              </div>
                            )}
                            <Button
                              size="sm"
                              className="mt-1 bg-orange-600 hover:bg-orange-700 text-xs h-7"
                              data-testid={`putaway-btn-${itemIdx}`}
                            >
                              <ScanLine className="w-3 h-3 mr-1" />
                              Put Away
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
      
      {/* Put Away Dialog */}
      <Dialog open={showPutAwayDialog} onOpenChange={setShowPutAwayDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white mx-4 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-400" />
              Put Away Item
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="py-4 space-y-4">
              {/* Item Info */}
              <div className="bg-gray-700 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-400 text-xs">USN/Pack</p>
                    <p className="font-mono font-bold">{selectedItem.usn_code || selectedItem.pack_barcode}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">SKU</p>
                    <p className="font-mono">{selectedItem.item_barcode}</p>
                  </div>
                  {selectedItem.design_name && (
                    <div className="col-span-2">
                      <p className="text-gray-400 text-xs">Design</p>
                      <p>{selectedItem.design_name}</p>
                    </div>
                  )}
                  {selectedItem.original_bin && (
                    <div className="col-span-2">
                      <p className="text-gray-400 text-xs">Original Bin (before cancel)</p>
                      <p className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        {selectedItem.original_bin}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Suggested Bin */}
              {loadingSuggestion ? (
                <div className="flex items-center justify-center py-3">
                  <RefreshCw className="w-5 h-5 text-blue-400 animate-spin mr-2" />
                  <span className="text-gray-400">Finding best bin...</span>
                </div>
              ) : suggestedBin ? (
                <div className="space-y-2">
                  <Alert className="bg-green-500/10 border-green-500/30">
                    <MapPin className="h-4 w-4 text-green-400" />
                    <AlertDescription className="text-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold">Suggested: </span>
                          <span 
                            className="font-mono text-lg cursor-pointer underline"
                            onClick={() => setBinCode(suggestedBin)}
                          >
                            {suggestedBin}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-500 text-green-400 hover:bg-green-500/20"
                          onClick={() => setBinCode(suggestedBin)}
                        >
                          Use
                        </Button>
                      </div>
                      {suggestionReason && (
                        <span className="text-xs block mt-1 text-green-300">{suggestionReason}</span>
                      )}
                    </AlertDescription>
                  </Alert>
                  
                  {/* Alternative Bins */}
                  {alternativeBins.length > 0 && (
                    <div className="bg-gray-700/50 rounded-lg p-2">
                      <p className="text-xs text-gray-400 mb-2">Alternative bins:</p>
                      <div className="flex flex-wrap gap-2">
                        {alternativeBins.map((alt, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="cursor-pointer hover:bg-gray-600 border-gray-500"
                            onClick={() => setBinCode(alt.bin_code)}
                          >
                            {alt.bin_code}
                            <span className="ml-1 text-xs text-gray-400">({alt.current_qty}/{alt.capacity})</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Alert className="bg-yellow-500/10 border-yellow-500/30">
                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                  <AlertDescription className="text-yellow-200 text-sm">
                    No suggested bin found. Please scan an appropriate bin.
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Bin Input */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Scan or Enter Bin Code</label>
                <Input
                  ref={scanInputRef}
                  value={binCode}
                  onChange={(e) => setBinCode(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder="Scan bin barcode..."
                  className="h-14 text-lg text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                  data-testid="cancelled-putaway-bin-input"
                />
              </div>
              
              {/* Help text */}
              {!suggestedBin && !loadingSuggestion && selectedItem.original_bin && (
                <p className="text-xs text-gray-500 text-center">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Original bin ({selectedItem.original_bin}) may be empty or have different items now.
                </p>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPutAwayDialog(false);
                setSelectedItem(null);
                setBinCode('');
              }}
              className="flex-1 border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePutAway}
              disabled={!binCode.trim() || putAwayLoading}
              className="flex-1 bg-orange-600 hover:bg-orange-700"
              data-testid="cancelled-putaway-confirm"
            >
              {putAwayLoading ? 'Storing...' : 'Put Away'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Pack Dialog — re-group returned pieces into a 5-pc pack */}
      <Dialog open={packDialogOpen} onOpenChange={setPackDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white mx-4 rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Boxes className="w-5 h-5 text-fuchsia-400" />
              Create {packSize}-pc Pack
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-xs text-gray-400">
              Scan the returned pieces, then scan the pre-printed pack code. They're still put away as usual — this just groups them so they pick as a pack later.
            </p>
            {packPieces[0] && (
              <div className="bg-gray-700 rounded-lg p-3" data-testid="cancelled-pack-product">
                <p className="text-xs text-gray-400">Product (locked)</p>
                <p className="font-semibold">{packPieces[0].design_name || packPieces[0].sku_code}</p>
                <p className="text-xs text-gray-400">{packPieces[0].sku_code} · {packPieces[0].shade} · {packPieces[0].size}</p>
              </div>
            )}
            <div className="flex items-center justify-between bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg px-3 py-2">
              <span className="text-sm text-fuchsia-300">Scanned</span>
              <span className="text-xl font-bold text-fuchsia-300" data-testid="cancelled-pack-count">{packPieces.length} / {packSize}</span>
            </div>
            <form onSubmit={scanPackPiece} className="flex gap-2">
              <Input ref={packPieceRef} value={packPieceInput} onChange={(e) => setPackPieceInput(e.target.value)}
                placeholder={packScanning ? 'Checking…' : 'Scan returned piece'}
                className="h-11 bg-gray-700 border-gray-600 font-mono" data-testid="cancelled-pack-piece-input"
                disabled={packScanning || packPieces.length >= packSize} />
              <Button type="submit" className="h-11 bg-fuchsia-600 hover:bg-fuchsia-700" disabled={packScanning || packPieces.length >= packSize} data-testid="cancelled-pack-piece-scan">
                <ScanLine className="w-5 h-5" />
              </Button>
            </form>
            {packPieces.length > 0 && (
              <div className="space-y-1 max-h-32 overflow-auto">
                {packPieces.map((p) => (
                  <div key={p.usn_code} className="flex items-center justify-between bg-gray-700/50 rounded px-2 py-1" data-testid={`cancelled-pack-row-${p.usn_code}`}>
                    <span className="font-mono text-xs">{p.usn_code}</span>
                    <button onClick={() => removePackPiece(p.usn_code)} className="text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={submitRepack} className="flex gap-2 pt-1">
              <Input ref={packCodeRef} value={packCode} onChange={(e) => setPackCode(e.target.value.toUpperCase())}
                placeholder="Scan pack code" className="h-11 bg-gray-700 border-gray-600 font-mono" data-testid="cancelled-pack-code-input" />
              <Button type="submit" className="h-11 bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap" disabled={packCreating || !packPieces.length} data-testid="cancelled-pack-create">
                <CheckCircle className="w-5 h-5 mr-1" /> Create
              </Button>
            </form>
            {packPieces.length > 0 && packPieces.length < packSize && (
              <p className="text-[11px] text-amber-400" data-testid="cancelled-pack-short-note">
                Short pack: {packPieces.length} of {packSize}. Scan the pack code to finish.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CancelledPickSlipPutAway;
